import React from 'react';
import { useAfiliado } from '../context/AfiliadoContext';

const WHATSAPP_NUMERO = '593993334684';

const CartPanel = ({
  abierto,
  onCerrar,
  cartItems = [],
  setCartItems,
  vistaAfiliado: propVistaAfiliado,
  afiliado: propAfiliado,
}) => {
  // Sincronización directa con useAfiliado con fallback a las props
  const ctx = useAfiliado();
  const afiliado = propAfiliado !== undefined ? propAfiliado : ctx?.afiliado;
  const vistaAfiliado = propVistaAfiliado !== undefined ? propVistaAfiliado : Boolean(ctx?.vistaAfiliado);
  const esModoAfiliado = Boolean(afiliado && vistaAfiliado);

  /**
   * Precio efectivo de un item:
   * - Modo afiliado: Busca precio explícito (incluyendo dentro de 'comercial') o calcula 25% OFF.
   * - Modo normal: Usa precio de oferta (si es válido) o el precio regular base.
   */
  const getPrecioEfectivo = (item) => {
    const precioBase = Number(item.precio || item.precioNormal || 0);
    const precioOferta = Number(item.precioOferta || 0);

    if (esModoAfiliado) {
      // 1. Busca en cualquier variante de nombre (raíz o dentro del mapa comercial)
      const precioAfilExplicito =
        item.precioAfiliado ??
        item.precio_afiliado ??
        item.comercial?.precio_afiliado ??
        item.comercial?.precioAfiliado;

      if (precioAfilExplicito != null && Number(precioAfilExplicito) > 0) {
        return Number(precioAfilExplicito);
      }

      // 2. Si no tiene precio fijo de afiliado, se aplica el 25% de descuento al precio regular
      if (precioBase > 0) {
        return precioBase * 0.75;
      }
    }

    return (precioOferta > 0 && precioOferta < precioBase)
      ? precioOferta
      : precioBase;
  };

  const total = cartItems.reduce(
    (acc, item) => acc + getPrecioEfectivo(item) * item.cantidad,
    0
  );

  const cantidadTotal = cartItems.reduce((acc, item) => acc + item.cantidad, 0);

  const actualizarCantidad = (id, delta) => {
    setCartItems(prev =>
      prev
        .map(item => item.id === id
          ? { ...item, cantidad: item.cantidad + delta }
          : item
        )
        .filter(item => item.cantidad > 0)
    );
  };

  const eliminarItem = (id) => {
    setCartItems(prev => prev.filter(item => item.id !== id));
  };

  const generarMensajeWhatsApp = () => {
    const codigoAfiliado = afiliado?.codigoDescuento || afiliado?.codigo_descuento || afiliado?.comercial?.codigo_descuento || '';
    let mensaje = esModoAfiliado
      ? `Hola, soy ${afiliado.nombre}${codigoAfiliado ? ` (Afiliado: ${codigoAfiliado})` : ' (Afiliado)'}. Quiero hacer este pedido:\n\n`
      : 'Hola, quiero hacer este pedido:\n\n';

    cartItems.forEach(item => {
      const precioFinal = getPrecioEfectivo(item);
      mensaje += `• ${item.nombre} x${item.cantidad} — $${(precioFinal * item.cantidad).toFixed(2)}\n`;
    });
    mensaje += `\nTotal: $${total.toFixed(2)}`;
    return `https://wa.me/${WHATSAPP_NUMERO}?text=${encodeURIComponent(mensaje)}`;
  };

  return (
    <>
      {/* Overlay */}
      <div
        onClick={onCerrar}
        style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'rgba(0,0,0,0.35)',
          zIndex: 1998,
          opacity: abierto ? 1 : 0,
          pointerEvents: abierto ? 'auto' : 'none',
          transition: 'opacity var(--transition-normal)',
        }}
      />

      {/* Panel lateral */}
      <aside
        role="dialog"
        aria-label="Carrito de compras"
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          height: '100dvh',
          width: 'min(400px, 100vw)',
          backgroundColor: 'var(--color-blanco)',
          zIndex: 1999,
          display: 'flex',
          flexDirection: 'column',
          fontFamily: 'var(--font-body)',
          transform: abierto ? 'translateX(0)' : 'translateX(100%)',
          transition: 'transform var(--transition-normal)',
          boxShadow: abierto ? 'var(--shadow-lg)' : 'none',
        }}
      >

        {/* Cabecera del panel */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: 'var(--space-4)', borderBottom: '1px solid var(--border-default)', flexShrink: 0,
        }}>
          <div>
            <h2 style={{
              margin: 0, fontFamily: 'var(--font-display)', fontSize: 'var(--font-size-h3)',
              fontWeight: 'var(--font-weight-black)', color: 'var(--text-primary)',
              letterSpacing: 'var(--letter-spacing-tight)',
            }}>
              Tu carrito
              {cantidadTotal > 0 && (
                <span style={{
                  marginLeft: 'var(--space-2)', fontSize: 'var(--font-size-xs)',
                  fontWeight: 'var(--font-weight-medium)', fontFamily: 'var(--font-body)',
                  color: 'var(--text-muted)',
                }}>
                  {cantidadTotal} {cantidadTotal === 1 ? 'producto' : 'productos'}
                </span>
              )}
            </h2>
            {esModoAfiliado && (
              <p style={{ margin: '2px 0 0', fontSize: 'var(--font-size-xs)', color: 'var(--color-verde, #16a34a)', fontWeight: 600 }}>
                🏷️ Precios de afiliado aplicados (25% OFF)
              </p>
            )}
          </div>
          <button onClick={onCerrar} aria-label="Cerrar carrito" style={{
            background: 'none', border: 'none', cursor: 'pointer', padding: 'var(--space-1)',
            color: 'var(--text-muted)', display: 'flex', WebkitTapHighlightColor: 'transparent',
          }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        {/* Lista de productos */}
        <div style={{ flex: 1, overflowY: 'auto', padding: 'var(--space-3) var(--space-4)' }}>
          {cartItems.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 'var(--space-8) 0', color: 'var(--text-muted)' }}>
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none"
                stroke="var(--color-gris-bd)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"
                style={{ marginBottom: 'var(--space-3)' }}>
                <circle cx="9" cy="21" r="1"/>
                <circle cx="20" cy="21" r="1"/>
                <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/>
              </svg>
              <p style={{ margin: 0, fontSize: 'var(--font-size-small)', fontWeight: 'var(--font-weight-light)' }}>
                Tu carrito está vacío
              </p>
            </div>
          ) : (
            cartItems.map(item => {
              const precioFinal = getPrecioEfectivo(item);
              const precioBase = Number(item.precio || item.precioNormal || 0);
              const imagenSrc = item.imagenUrl || item.imagen;

              return (
                <div key={item.id} style={{
                  display: 'flex',
                  gap: 'var(--space-3)',
                  padding: 'var(--space-3) 0',
                  borderBottom: '0.5px solid var(--border-default)',
                  alignItems: 'center',
                }}>

                  {/* Miniatura */}
                  <div className="cart-item-img" style={{
                    width: '54px',
                    height: '54px',
                    flexShrink: 0,
                    backgroundColor: 'var(--color-blanco)',
                    border: '0.5px solid var(--border-default)',
                    borderRadius: 'var(--radius-md)',
                    overflow: 'hidden',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}>
                    {imagenSrc ? (
                      <img
                        src={imagenSrc}
                        alt={item.nombre}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'contain',
                          padding: '3px',
                        }}
                      />
                    ) : (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
                        stroke="var(--color-gris-bd)" strokeWidth="1.5">
                        <rect x="3" y="3" width="18" height="18" rx="2"/>
                        <circle cx="8.5" cy="8.5" r="1.5"/>
                        <path d="M21 15l-5-5L5 21"/>
                      </svg>
                    )}
                  </div>

                  {/* Info */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p className="cart-item-nombre" style={{
                      margin: '0 0 2px',
                      fontSize: 'var(--font-size-xs)',
                      fontWeight: 'var(--font-weight-medium)',
                      color: 'var(--text-primary)',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}>
                      {item.nombre}
                    </p>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px' }}>
                      <p className="cart-item-precio" style={{
                        margin: 0,
                        fontSize: 'var(--font-size-xs)',
                        color: esModoAfiliado ? 'var(--color-verde, #16a34a)' : 'var(--text-muted)',
                        fontWeight: esModoAfiliado ? 'var(--font-weight-bold, 700)' : 'var(--font-weight-regular)',
                      }}>
                        ${Number(precioFinal).toFixed(2)} c/u
                      </p>
                      {esModoAfiliado && precioBase > precioFinal && (
                        <span style={{ fontSize: '11px', color: 'var(--text-muted)', textDecoration: 'line-through' }}>
                          ${precioBase.toFixed(2)}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Controles */}
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--space-2)',
                    flexShrink: 0,
                  }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      border: '0.5px solid var(--border-default)',
                      borderRadius: 'var(--radius-pill)',
                      overflow: 'hidden',
                    }}>
                      <BtnCantidad onClick={() => actualizarCantidad(item.id, -1)} label="Quitar uno">−</BtnCantidad>
                      <span className="cart-item-cantidad" style={{
                        minWidth: '24px',
                        textAlign: 'center',
                        fontSize: 'var(--font-size-xs)',
                        fontWeight: 'var(--font-weight-medium)',
                        color: 'var(--text-primary)',
                      }}>
                        {item.cantidad}
                      </span>
                      <BtnCantidad onClick={() => actualizarCantidad(item.id, 1)} label="Agregar uno">+</BtnCantidad>
                    </div>

                    <button
                      onClick={() => eliminarItem(item.id)}
                      aria-label={`Eliminar ${item.nombre}`}
                      className="cart-btn-eliminar"
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        padding: '4px',
                        color: 'var(--text-disabled)',
                        display: 'flex',
                        transition: 'color var(--transition-fast)',
                        WebkitTapHighlightColor: 'transparent',
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.color = '#e02424'}
                      onMouseLeave={(e) => e.currentTarget.style.color = 'var(--text-disabled)'}
                    >
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
                        stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M3 6h18"/>
                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"/>
                        <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                      </svg>
                    </button>
                  </div>

                </div>
              );
            })
          )}
        </div>

        {/* Pie con total y CTA */}
        {cartItems.length > 0 && (
          <div style={{
            padding: 'var(--space-4)',
            borderTop: '1px solid var(--border-default)',
            flexShrink: 0,
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'baseline',
              marginBottom: 'var(--space-3)',
            }}>
              <span style={{
                fontSize: 'var(--font-size-small)',
                fontWeight: 'var(--font-weight-medium)',
                color: 'var(--text-secondary)',
              }}>
                Total
              </span>
              <span style={{
                fontFamily: 'var(--font-display)',
                fontSize: 'var(--font-size-h2)',
                fontWeight: 'var(--font-weight-black)',
                color: 'var(--text-primary)',
                letterSpacing: 'var(--letter-spacing-tight)',
              }}>
                ${total.toFixed(2)}
              </span>
            </div>

            {/* Leyenda del proceso de compra */}
            <p style={{
              margin: '0 0 var(--space-3)',
              fontSize: 'var(--font-size-xs)',
              fontWeight: 'var(--font-weight-light)',
              color: 'var(--text-muted)',
              textAlign: 'center',
              lineHeight: 'var(--line-height-normal)',
            }}>
              Todas las compras se concretan por WhatsApp.
              Al enviar tu pedido, te atenderemos personalmente para coordinar el pago y la entrega.
            </p>

            <a
              href={generarMensajeWhatsApp()}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                width: '100%',
                padding: '13px var(--space-4)',
                backgroundColor: 'var(--color-verde)',
                color: 'var(--color-blanco)',
                border: 'none',
                borderRadius: 'var(--radius-md)',
                fontFamily: 'var(--font-body)',
                fontSize: 'var(--font-size-small)',
                fontWeight: 'var(--font-weight-medium)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 'var(--space-2)',
                textDecoration: 'none',
                boxSizing: 'border-box',
                transition: 'background-color var(--transition-fast)',
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--color-verde-hover)'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'var(--color-verde)'}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
              Enviar pedido por WhatsApp
            </a>
          </div>
        )}

        <style>{`
          /* Móvil: elementos más grandes y cómodos al tacto */
          @media (max-width: 600px) {
            .cart-item-img {
              width: 68px !important;
              height: 68px !important;
            }
            .cart-item-nombre {
              font-size: var(--font-size-small) !important;
              white-space: normal !important;
              display: -webkit-box;
              -webkit-line-clamp: 2;
              -webkit-box-orient: vertical;
            }
            .cart-item-precio {
              font-size: var(--font-size-small) !important;
            }
            .cart-item-cantidad {
              min-width: 30px !important;
              font-size: var(--font-size-small) !important;
            }
            .cart-btn-cantidad {
              width: 36px !important;
              height: 36px !important;
              font-size: 18px !important;
            }
            .cart-btn-eliminar {
              padding: 8px !important;
            }
            .cart-btn-eliminar svg {
              width: 18px;
              height: 18px;
            }
          }
        `}</style>

      </aside>
    </>
  );
};

const BtnCantidad = ({ onClick, children, label }) => (
  <button
    onClick={onClick}
    aria-label={label}
    className="cart-btn-cantidad"
    style={{
      width: '26px',
      height: '26px',
      border: 'none',
      background: 'none',
      cursor: 'pointer',
      fontSize: '15px',
      lineHeight: 1,
      color: 'var(--text-secondary)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'background-color var(--transition-fast)',
      WebkitTapHighlightColor: 'transparent',
    }}
    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--color-gris-fondo)'}
    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
  >
    {children}
  </button>
);

export default CartPanel;